import { Fragment } from "react";
import { VariantTitle } from "../../components/VariantTitle";
import { ReviewRatingWrapper } from "../../components/ReviewRatingWrapper";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { getTokenValue, contentfulToImageData, formatCTAS, formatTiles, replaceToken } from "../../functions";
import { CallToAction } from "../../components/CTA/CallToActionButton";
import { UserTestimonials } from "../../components/UserTestimonials";
import { PageSectionWrapper } from "../../components/PageSectionWrapper";
import { ListTile } from "../../components/Tiles/ListTile";
import { formatBannerMediaData } from "..";

const PartnersRow = ({ formattedPartnersImages, parsedClasses = "", titleClass = "", imgWrapperClass="justify-center flex-wrap gap-8 md:gap-[64px]", partnerHeaderText="We're partnered with:" }) => {
	if (!formattedPartnersImages?.length) return null;
	return (
		<div className={`flex flex-col text-center py-8 w-full ${parsedClasses}`}>
			<span className={`${titleClass || "text-is2-gray-150"}`}>{partnerHeaderText}</span>
			<div className={`flex ${imgWrapperClass}`}>
				{formattedPartnersImages.map((partner, index) => {
					const link =  /^https?:\/\//.test(partner.description) && partner.description;
					const img = <img
									src={partner?.imageUrl}
									alt={partner?.alt}
									className="h-[42px]"
								/>
					return link ? <a href={link} key={index} target="_blank" className="shrink-0">{img}</a> : <Fragment key={index}>{img}</Fragment>
				})}
			</div>
		</div>
	);
};

export const JoinUsPageSection = ({
	headerText,
	partnerHeaderText,
	variant,
	subtitle,
	subheading,
	userTestimonials,
	reviewCount,
	rating,
	ourPartners,
	ctas,
	siteStatus,
	isStatic = false,
	tiles = null,
	contentDisplayVariant,
	imageBanner,
}) => {
	const formattedUserTestimonials =
		userTestimonials?.map(({ fields }) => documentToReactComponents(fields.paragraph)) || [];
	const formattedPartnersImages = ourPartners?.map(partner => contentfulToImageData(partner)) || [];
	const formattedCtas = formatCTAS(ctas, siteStatus, isStatic);
	const ratingValue = getTokenValue(rating);
	const reviewCountValue = getTokenValue(reviewCount);
	const formattedTitle = headerText && replaceToken(headerText, "customer_total", siteStatus?.customer_total);
	const formattedTiles = formatTiles(tiles, siteStatus);

	switch (contentDisplayVariant) {
		case "Banner": {
			const bannerImage = imageBanner?.[0] ? contentfulToImageData(imageBanner[0]) : null;
			const overlayText = imageBanner?.[0]?.fields?.description || null;
			return (
				<PageSectionWrapper variant={variant} parsedClasses="!p-0">
					<div className="flex flex-col md:flex-row w-screen relative left-1/2 -translate-x-1/2">
						<div className="relative w-full md:w-1/2 md:min-h-[300px] overflow-hidden">
							{bannerImage && (
								<>
									<img
										src={bannerImage.imageUrl}
										alt={bannerImage.alt || "Banner"}
										className="w-full h-full object-cover"
									/>
									{overlayText && (
										<div className="absolute left-1/2 -translate-x-1/2 md:left-8 md:translate-x-0 bottom-8 py-2 px-4 bg-is2-blue text-white rounded-sm">
											{overlayText}
										</div>
									)}
								</>
							)}
						</div>
						<div className="w-full md:w-1/2 bg-is2-blue text-white flex flex-col justify-center px-4 py-8 md:p-8">
							<VariantTitle
								title={formattedTitle}
								subtitle={subtitle}
								parsedClasses="text-center !pb-6"
							/>
							{ratingValue && <ReviewRatingWrapper rating={ratingValue} reviewCount={reviewCountValue} clsn={["[&_p]:!text-white"]} />}
							{formattedCtas && (
								<div className="flex flex-col md:flex-row gap-4 py-8 justify-center items-center">
									{formattedCtas.map((cta, index) => (
										<div key={index} className="w-full md:w-auto">
											<CallToAction {...cta} />
										</div>
									))}
								</div>
							)}
						</div>
					</div>
					{formattedPartnersImages.length>0 && <div className="w-screen relative left-1/2 -translate-x-1/2 bg-is2-yellow-100 py-8 px-4">
						<PartnersRow formattedPartnersImages={formattedPartnersImages} parsedClasses="md:py-0 gap-4 mt-0" />
					</div>}
				</PageSectionWrapper>
			);
		}
		case "Partners": {
			return (
				<PageSectionWrapper variant={variant} parsedClasses="!p-0">
					<PartnersRow formattedPartnersImages={formattedPartnersImages} partnerHeaderText={partnerHeaderText} parsedClasses="lg:flex-row justify-start md:justify-center gap-8" titleClass="!text-is2-gray-700 text-is2_xl md:text-is2_2xl" imgWrapperClass="gap-8 overflow-x-auto no-scrollbar px-8 md:px-0" />
				</PageSectionWrapper>
			)
		}

		default:
			const { imageComponent } = formatBannerMediaData(null, imageBanner, null, null, "mx-auto");
			return (
				<PageSectionWrapper variant={variant}>
					<VariantTitle title={formattedTitle} subtitle={subtitle} parsedClasses="md:text-center !pb-6" />
					{ratingValue && <ReviewRatingWrapper rating={ratingValue} reviewCount={reviewCountValue} />}
					{imageComponent}
					{formattedUserTestimonials.length > 0 && (
						<div className="hidden md:block">
							<UserTestimonials userTestimonials={formattedUserTestimonials} />
						</div>
					)}
					{formattedCtas.length > 0 && (
						<div className="flex flex-col md:flex-row gap-4 pt-16 pb-8 justify-center">
							{formattedCtas.map((cta, index) => (
								<div key={index} className="w-full md:w-auto">
									<CallToAction {...cta} />
								</div>
							))}
						</div>
					)}
					{formattedUserTestimonials.length > 0 && (
						<div className="block md:hidden">
							<UserTestimonials userTestimonials={formattedUserTestimonials} />
						</div>
					)}
					{subheading && <h3 className="!text-is2_2xl mt-0 md:mt-8 text-center">{subheading} </h3>}
			
					{formattedTiles?.length > 0 && (
						<div className="md:max-w-fit md:mx-auto">
							<ListTile tiles={formattedTiles} isVertical={false} />
						</div>
					)}
					<PartnersRow formattedPartnersImages={formattedPartnersImages} parsedClasses={"md:py-0 gap-4 mt-8"}/>
				</PageSectionWrapper>
			);
	}
};
